import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

// What the browser tab, Google and AI assistants are told about each page.
// Song pages say their own words (see Watch.js); everything else is set here.
// The title is the blue link in Google, the description is the small gray text under it, and the
// "canonical" address tells Google the one right web address for the page (nobody sees it).

export const SITE = "https://danthemantv.com";
const NAME = "Dan The Man TV";
export const HOME_TITLE = `${NAME} – Original Music Videos`;
export const HOME_DESC = "Original music and music videos written and produced by Dan The Man with A.I. assistance. Free previews, no ads.";

const PAGES = {
  "/videos": [
    `Music Videos | ${NAME}`,
    `Watch every original music video on ${NAME}, written and produced by Dan The Man with A.I. assistance. Filter by genre and mood. Free previews, no ads.`,
  ],
  "/genres": [
    `Genres | ${NAME}`,
    "Pick a genre and watch original music videos by Dan The Man. Free previews, no ads.",
  ],
  "/about": [
    `About | ${NAME}`,
    `${NAME} is a music and music video channel from Austin, Texas, and the Texas Hill Country. Original songs written and produced by Dan The Man with A.I. assistance.`,
  ],
  "/terms": [`Terms | ${NAME}`, `The rules for using ${NAME}.`],
  "/privacy": [`Privacy | ${NAME}`, `How ${NAME} handles visits, accounts and email.`],
};

const GENRE_PATH = /^\/genres\/([a-z0-9][a-z0-9-]{0,59})$/;
const WATCH_PATH = /^\/watch\/[A-Za-z0-9_-]{1,64}$/;

// "Funk" -> "Funk Music Videos". A genre that already ends in "Music" does not say it twice.
export function genreWords(name) {
  const n = String(name || "").replace(/\s+/g, " ").trim();
  if (!n) return null;
  const endsInMusic = /\bmusic$/i.test(n);
  return {
    title: `${n}${endsInMusic ? "" : " Music"} Videos | ${NAME}`,
    description: `Watch ${n}${endsInMusic ? "" : " music"} videos on ${NAME}, written and produced by Dan The Man with A.I. assistance. Free previews, no ads.`,
  };
}

function descriptionTag() {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
  return tag;
}

function put(title, description) {
  document.title = title;
  descriptionTag().setAttribute("content", description);
}

function putAddress(url) {
  let link = document.querySelector('link[rel="canonical"]');
  if (url) {
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", url);
  } else if (link) {
    link.remove();
  }
  const og = document.querySelector('meta[property="og:url"]');
  if (og) og.setAttribute("content", url || `${SITE}/`);
}

// The genre's real name comes from the same list the Genres page uses.
function genreName(slug) {
  return api.get("/genres")
    .then((r) => ((Array.isArray(r.data) ? r.data : []).find((g) => g.slug === slug) || {}).name || "")
    .catch(() => "");
}

export default function PageSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/";
    let dead = false;
    const page = PAGES[path];
    // A page with no words of its own (Home, a song, a genre until its name arrives, Login, Account...) gets the site's own.
    if (page) put(page[0], page[1]); else put(HOME_TITLE, HOME_DESC);
    const isGenre = GENRE_PATH.exec(path);
    const isPublic = path === "/" || !!page || !!isGenre || WATCH_PATH.test(path);
    putAddress(isPublic ? `${SITE}${path === "/" ? "/" : path}` : null);
    if (isGenre) {
      genreName(isGenre[1]).then((name) => {
        const words = genreWords(name);
        if (!dead && words) put(words.title, words.description);
      });
    }
    return () => { dead = true; };
  }, [pathname]);
  return null;
}
